import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { get } from "lodash";
import { FocusableItem } from "@szhsin/react-menu";
import Table from "components/Table";
import Pagination from "components/Pagination";
import ActionsMenu from "components/ActionsMenu";
import { apiFetch, formatDateTime, getTotalPages, titleCase } from "utils";
import { useAuthState, useAuthDispatch } from "context";

const getResyncJobs = ({ token, dispatch, page }) => {
  return apiFetch({
    token,
    dispatch,
    endpoint: `/api/internal/shelterluv/resync_jobs?page=${page}`,
    includeMeta: true,
  });
};

const ResyncJobs = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const {
    data: response = { data: [], meta: { stats: { total: { count: 0 } } } },
    error,
    isLoading,
  } = useQuery(
    ["resyncJobs", { page }],
    () => getResyncJobs({ token, dispatch, page }),
    {
      keepPreviousData: true,
      refetchInterval: 5000,
    }
  );

  useEffect(() => {
    if (totalPages > page) {
      queryClient.prefetchQuery(["resyncJobs", { page: page + 1 }], () =>
        getResyncJobs({ token, dispatch, page: page + 1 })
      );
    }
  }, [response, page, queryClient, token, dispatch, totalPages]);

  useEffect(() => {
    const totalCount = get(response, "meta.stats.total.count", 0);
    const calculatedPages = getTotalPages({ totalCount });
    setTotalPages(calculatedPages);
  }, [response]);

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      running: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {titleCase(status)}
      </span>
    );
  };

  const getStepsProgress = (steps) => {
    if (!steps) return "0/3";
    const total = Object.keys(steps).length;
    const completed = Object.values(steps).filter(
      (step) => step === "completed"
    ).length;
    return `${completed}/${total}`;
  };

  const columns = [
    {
      accessor: "id",
      Header: "Job ID",
      Cell: ({ cell }) => `#${cell.value}`,
    },
    {
      accessor: "attributes.dateFrom",
      Header: "Date From",
      Cell: ({ cell }) => new Date(cell.value).toLocaleDateString(),
    },
    {
      accessor: "attributes.status",
      Header: "Status",
      Cell: ({ cell }) => getStatusBadge(cell.value),
    },
    {
      accessor: "attributes.steps",
      Header: "Steps",
      Cell: ({ cell }) => getStepsProgress(cell.value),
    },
    {
      accessor: "attributes.progress",
      Header: "Progress",
      Cell: ({ cell }) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[60px]">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${cell.value}%` }}
            />
          </div>
          <span className="text-sm text-gray-600 whitespace-nowrap">
            {cell.value}%
          </span>
        </div>
      ),
    },
    {
      accessor: "attributes.errorMessage",
      Header: "Error",
      Cell: ({ cell }) => {
        if (!cell.value) return <span className="text-gray-400">-</span>;
        const truncatedError =
          cell.value.length > 50
            ? cell.value.substring(0, 50) + "..."
            : cell.value;
        return (
          <span className="text-red-600 text-sm cursor-help" title={cell.value}>
            {truncatedError}
          </span>
        );
      },
    },
    {
      accessor: "attributes.createdAt",
      Header: "Created At",
      Cell: ({ cell }) => formatDateTime(cell.value),
    },
    {
      accessor: "actions",
      Header: "",
      Cell: ({ row: { original } }) => (
        <ActionsMenu>
          <FocusableItem>
            {({ ref }) => (
              <Link to={`/api-management/resync-jobs/${original.id}`} ref={ref}>
                View Details
              </Link>
            )}
          </FocusableItem>
        </ActionsMenu>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h1>Resync Jobs History</h1>
        <Link to="/api-management/resync">
          <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            New Resync
          </button>
        </Link>
      </div>

      <Table
        data={response.data || []}
        columns={columns}
        columnWidths={[null, null, null, null, "w-48", "w-64", null, "w-16"]}
        noResultsText="No resync jobs found"
        isLoading={isLoading}
        error={error}
      />

      {!!(response.data || []).length && totalPages > 1 && (
        <div className="flex justify-end mt-8">
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            onPageChanged={(newPage) => setPage(newPage)}
          />
        </div>
      )}
    </>
  );
};

export default ResyncJobs;
